import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Archive, Database } from 'lucide-react';
import { formatBytes } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { cn } from '@/lib/utils';

const REFETCH = 60_000;

function ageColor(ageSec: number): string {
  if (ageSec < 86400) return 'text-emerald-400';
  if (ageSec < 86400 * 3) return 'text-sky-400';
  if (ageSec < 86400 * 7) return 'text-amber-400';
  return 'text-rose-400';
}

function ageLabel(ageSec: number): string {
  if (ageSec < 60) return 'just now';
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}h ago`;
  return `${Math.floor(ageSec / 86400)}d ago`;
}

export function BackupsWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['proxmox', 'backups'],
    queryFn: endpoints.backups,
    refetchInterval: REFETCH,
  });
  const backups = q.data?.backups ?? [];

  // Latest per VMID
  const latestByVmid = new Map<number, (typeof backups)[number]>();
  for (const b of backups) {
    if (b.vmid === null) continue;
    const existing = latestByVmid.get(b.vmid);
    if (!existing || b.ctime > existing.ctime) latestByVmid.set(b.vmid, b);
  }
  const rows = Array.from(latestByVmid.values()).sort((a, b) => b.ctime - a.ctime);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Archive className="h-3.5 w-3.5" />
          PVE backups
        </CardTitle>
        <span className="text-xs text-muted-foreground">{backups.length} total</span>
      </CardHeader>
      <CardBody>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'no vzdump backups found'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {rows.map((b) => {
              const age = Math.max(0, Math.floor(now / 1000) - b.ctime);
              return (
                <div
                  key={b.volid}
                  className="flex items-center justify-between gap-2 text-xs rounded-md border border-border bg-card/60 px-2 py-1.5"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Database className="h-3 w-3 text-muted-foreground" />
                    <span className="font-mono text-foreground/80">
                      vmid {b.vmid}
                    </span>
                    <span className="text-muted-foreground truncate">
                      {b.volid.split('/').pop()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 tabular-nums">
                    <span className="text-muted-foreground">{formatBytes(b.size)}</span>
                    <span className={cn('font-semibold', ageColor(age))}>
                      {ageLabel(age)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
